import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  FileText,
  Upload,
  Settings,
  TrendingUp,
  Clock,
  Globe,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

import AdminSuppliersPage from "@/pages/AdminSuppliersPage";
import TopTexCatalogPage from "@/pages/TopTexCatalogPage";

// Dashboard Component
const Dashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (e) {
      console.error("Error fetching stats:", e);
    }
    setLoading(false);
  };

  const statCards = [
    { title: "Utilisateurs", value: stats?.total_users || 0, icon: Users, color: "#1D3557" },
    { title: "Produits", value: stats?.total_products || 0, icon: Package, color: "#8C9D78" },
    { title: "Commandes", value: stats?.total_orders || 0, icon: ShoppingCart, color: "#C65D47" },
    { title: "Devis", value: stats?.total_quotes || 0, icon: FileText, color: "#E9C46A" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <h1 className="text-3xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
        Tableau de bord
      </h1>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-[#1D3557]">
                    {loading ? "..." : stat.value}
                  </p>
                </div>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#C65D47]" />
              Commandes en attente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-[#C65D47]">
              {loading ? "..." : stats?.pending_orders || 0}
            </p>
            <p className="text-sm text-stone-500">commandes à traiter</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#8C9D78]" />
              Devis en attente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-[#8C9D78]">
              {loading ? "..." : stats?.pending_quotes || 0}
            </p>
            <p className="text-sm text-stone-500">devis à traiter</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Products Management
const ProductsAdmin = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfResult, setPdfResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?show_all=true`);
      setProducts(response.data);
    } catch (e) {
      console.error("Error fetching products:", e);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Produit supprimé");
      fetchProducts();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const togglePublish = async (id) => {
    try {
      const res = await axios.patch(`${API}/products/${id}/toggle-publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.published ? "Produit publié" : "Produit dépublié");
      fetchProducts();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleSave = async (productData, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Produit créé !");
      } else {
        await axios.put(`${API}/products/${editProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Produit mis à jour !");
      }
      setEditProduct(null);
      setShowCreateModal(false);
      fetchProducts();
    } catch (e) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfUploading(true);
    setPdfResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/products/import-pdf`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 120000
      });
      setPdfResult(res.data);
      toast.success(`${res.data.imported} produits importés du PDF !`);
      fetchProducts();
    } catch (e) {
      toast.error("Erreur lors de l'import PDF");
    }
    setPdfUploading(false);
    e.target.value = "";
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(price);
  };

  const filteredProducts = products.filter(p =>
    !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || (p.sku || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h1 className="text-3xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
          Produits ({products.length})
        </h1>
        <div className="flex flex-wrap gap-3">
          <label className="cursor-pointer">
            <input type="file" accept=".pdf" className="hidden" onChange={handlePdfImport} disabled={pdfUploading} data-testid="pdf-import-input" />
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${pdfUploading ? "bg-stone-100 text-stone-400" : "border-[#C65D47] text-[#C65D47] hover:bg-[#C65D47] hover:text-white"}`}>
              <FileText className="w-4 h-4" />
              {pdfUploading ? "Import en cours..." : "Importer PDF"}
            </span>
          </label>
          <Link to="/admin/import">
            <Button variant="outline" className="rounded-full">
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
          </Link>
          <Button onClick={() => setShowCreateModal(true)} className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full" data-testid="add-product-btn">
            <Package className="w-4 h-4 mr-2" />
            Ajouter un produit
          </Button>
        </div>
      </div>

      {pdfResult && (
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="p-4">
            <p className="font-semibold text-green-800">{pdfResult.imported} produits importés sur {pdfResult.total_found} trouvés</p>
            {pdfResult.errors?.length > 0 && (
              <details className="mt-2">
                <summary className="text-sm text-amber-600 cursor-pointer">{pdfResult.errors.length} avertissements</summary>
                <ul className="text-xs text-stone-500 mt-1 space-y-1">
                  {pdfResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      <div className="relative max-w-md">
        <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input
          placeholder="Rechercher par nom ou référence..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 rounded-full"
          data-testid="product-search"
        />
      </div>

      <Card className="border-0 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produit</TableHead>
              <TableHead>Fournisseur</TableHead>
              <TableHead>Prix</TableHead>
              <TableHead>Paliers</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">Chargement...</TableCell>
              </TableRow>
            ) : filteredProducts.map((product) => (
              <TableRow key={product.id} className={!product.published ? "opacity-50" : ""}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center">
                        <Package className="w-5 h-5 text-stone-300" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">{product.name}</p>
                      <p className="text-xs text-stone-400">{product.sku}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{product.supplier || "—"}</TableCell>
                <TableCell className="text-sm font-medium">{product.price > 0 ? formatPrice(product.price) : "—"}</TableCell>
                <TableCell>
                  {product.pricing_tiers?.length > 0 ? (
                    <Badge variant="secondary" className="text-xs">{product.pricing_tiers.length} palier(s)</Badge>
                  ) : "—"}
                </TableCell>
                <TableCell>
                  <Badge variant={product.stock > 50 ? "default" : "destructive"}>{product.stock}</Badge>
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => togglePublish(product.id)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${product.published !== false ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}
                    data-testid={`toggle-publish-${product.id}`}
                  >
                    {product.published !== false ? "Publié" : "Brouillon"}
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button onClick={() => setEditProduct(product)} variant="ghost" size="sm" className="text-[#1D3557]" data-testid={`edit-${product.id}`}>
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => handleDelete(product.id)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Edit / Create Modal */}
      {(editProduct || showCreateModal) && (
        <ProductModal
          product={editProduct}
          categories={categories}
          onSave={handleSave}
          onClose={() => { setEditProduct(null); setShowCreateModal(false); }}
          isNew={showCreateModal}
        />
      )}
    </div>
  );
};

// Product Edit/Create Modal
const ProductModal = ({ product, categories, onSave, onClose, isNew }) => {
  const [form, setForm] = useState({
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price || 0,
    category_id: product?.category_id || "",
    image_url: product?.image_url || "",
    images: product?.images || [],
    supplier: product?.supplier || "",
    customizable: product?.customizable ?? true,
    min_quantity: product?.min_quantity || 1,
    stock: product?.stock || 100,
    sku: product?.sku || "",
    tags: product?.tags || [],
    pricing_tiers: product?.pricing_tiers?.length > 0 ? product.pricing_tiers : [],
    published: product?.published ?? true,
    colors: product?.colors || [],
    sizes: product?.sizes || [],
    color_images: product?.color_images || {},
  });
  const [colorInput, setColorInput] = useState("");
  const [sizeInput, setSizeInput] = useState("");

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const addTier = () => {
    const tiers = [...form.pricing_tiers];
    const lastMax = tiers.length > 0 ? tiers[tiers.length - 1].max_qty + 1 : 1;
    tiers.push({ min_qty: lastMax, max_qty: lastMax + 49, price: 0 });
    updateField("pricing_tiers", tiers);
  };

  const updateTier = (index, field, value) => {
    const tiers = [...form.pricing_tiers];
    tiers[index] = { ...tiers[index], [field]: Number(value) };
    updateField("pricing_tiers", tiers);
  };

  const removeTier = (index) => {
    updateField("pricing_tiers", form.pricing_tiers.filter((_, i) => i !== index));
  };

  const addColor = () => {
    if (colorInput.trim() && !form.colors.includes(colorInput.trim())) {
      updateField("colors", [...form.colors, colorInput.trim()]);
      setColorInput("");
    }
  };

  const addSize = () => {
    if (sizeInput.trim() && !form.sizes.includes(sizeInput.trim())) {
      updateField("sizes", [...form.sizes, sizeInput.trim()]);
      setSizeInput("");
    }
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    const basePrice = form.pricing_tiers.length > 0 ? Math.min(...form.pricing_tiers.map(t => t.price)) : form.price;
    onSave({ ...form, price: basePrice || form.price }, isNew);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="product-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
            {isNew ? "Ajouter un produit" : "Modifier le produit"}
          </h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600 text-xl" data-testid="close-modal">X</button>
        </div>

        <div className="p-6 space-y-5">
          {/* Image preview */}
          {form.image_url && (
            <div className="flex justify-center">
              <img src={form.image_url} alt="Aperçu" className="h-32 object-contain rounded-lg" />
            </div>
          )}

          {/* Name */}
          <div>
            <Label>Nom du produit</Label>
            <Input value={form.name} onChange={(e) => updateField("name", e.target.value)} data-testid="product-name-input" />
          </div>

          {/* Description */}
          <div>
            <Label>Description</Label>
            <textarea
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              className="w-full min-h-[80px] p-3 border rounded-lg text-sm"
              data-testid="product-desc-input"
            />
          </div>

          {/* Row: Category + Supplier */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category_id} onValueChange={(v) => updateField("category_id", v)}>
                <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fournisseur</Label>
              <Input value={form.supplier} onChange={(e) => updateField("supplier", e.target.value)} />
            </div>
          </div>

          {/* Row: Image URL + SKU */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>URL de l'image</Label>
              <Input value={form.image_url} onChange={(e) => updateField("image_url", e.target.value)} placeholder="https://..." />
            </div>
            <div>
              <Label>Référence (SKU)</Label>
              <Input value={form.sku} onChange={(e) => updateField("sku", e.target.value)} />
            </div>
          </div>

          {/* Row: Stock + Min Qty + Price */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Stock</Label>
              <Input type="number" value={form.stock} onChange={(e) => updateField("stock", parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <Label>Qté minimum</Label>
              <Input type="number" value={form.min_quantity} onChange={(e) => updateField("min_quantity", parseInt(e.target.value) || 1)} />
            </div>
            <div>
              <Label>Prix unitaire (€)</Label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => updateField("price", parseFloat(e.target.value) || 0)} />
            </div>
          </div>

          {/* Colors */}
          <div>
            <Label>Couleurs disponibles</Label>
            <div className="flex gap-2 mt-1">
              <Input value={colorInput} onChange={(e) => setColorInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addColor())} placeholder="Ex: Rouge, Bleu..." className="flex-1" />
              <Button type="button" variant="outline" size="sm" onClick={addColor}>+</Button>
            </div>
            {form.colors.length > 0 && (
              <div className="space-y-3 mt-3">
                {form.colors.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                    {form.color_images[c] && (
                      <img src={form.color_images[c]} alt={c} className="w-10 h-10 rounded-lg object-cover" />
                    )}
                    <span className="font-medium text-sm min-w-[80px]">{c}</span>
                    <Input
                      value={form.color_images[c] || ""}
                      onChange={(e) => updateField("color_images", { ...form.color_images, [c]: e.target.value })}
                      placeholder="URL de l'image pour cette couleur"
                      className="flex-1 h-9 text-xs"
                    />
                    <button onClick={() => {
                      const newColors = form.colors.filter((_, j) => j !== i);
                      const newImages = { ...form.color_images };
                      delete newImages[c];
                      setForm(prev => ({ ...prev, colors: newColors, color_images: newImages }));
                    }} className="text-stone-400 hover:text-red-500 text-lg px-1">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sizes */}
          <div>
            <Label>Tailles disponibles</Label>
            <div className="flex gap-2 mt-1">
              <Input value={sizeInput} onChange={(e) => setSizeInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSize())} placeholder="Ex: S, M, L, XL..." className="flex-1" />
              <Button type="button" variant="outline" size="sm" onClick={addSize}>+</Button>
            </div>
            {form.sizes.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.sizes.map((s, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    {s}
                    <button onClick={() => updateField("sizes", form.sizes.filter((_, j) => j !== i))} className="ml-1 hover:text-red-500">×</button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Pricing Tiers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#8C9D78]" />
                Prix par quantité
              </Label>
              <Button type="button" variant="outline" size="sm" onClick={addTier} className="text-xs" data-testid="add-tier-btn">
                + Ajouter un palier
              </Button>
            </div>
            {form.pricing_tiers.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-xs text-stone-500 px-1">
                  <span>De (qté)</span><span>À (qté)</span><span>Prix HT (€)</span><span></span>
                </div>
                {form.pricing_tiers.map((tier, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`tier-row-${i}`}>
                    <Input type="number" value={tier.min_qty} onChange={(e) => updateTier(i, "min_qty", e.target.value)} className="h-9" />
                    <Input type="number" value={tier.max_qty} onChange={(e) => updateTier(i, "max_qty", e.target.value)} className="h-9" />
                    <Input type="number" step="0.01" value={tier.price} onChange={(e) => updateTier(i, "price", e.target.value)} className="h-9" />
                    <button onClick={() => removeTier(i)} className="text-stone-400 hover:text-red-500 px-2">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Publish toggle */}
          <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
            <button
              type="button"
              onClick={() => updateField("published", !form.published)}
              className={`relative w-12 h-6 rounded-full transition-colors ${form.published ? "bg-[#8C9D78]" : "bg-stone-300"}`}
              data-testid="publish-toggle"
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.published ? "left-6" : "left-0.5"}`} />
            </button>
            <span className="text-sm font-medium text-[#1D3557]">
              {form.published ? "Publié — visible en boutique" : "Brouillon — non visible"}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 p-6 border-t">
          <Button variant="outline" onClick={onClose} className="rounded-full">Annuler</Button>
          <Button onClick={handleSubmit} className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full" data-testid="save-product-btn">
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

// Orders Management
const OrdersAdmin = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (e) {
      console.error("Error fetching orders:", e);
    }
    setLoading(false);
  };

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Statut mis à jour");
      fetchOrders();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <h1 className="text-3xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
        Commandes
      </h1>

      <Card className="border-0 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Commande</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Statut</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">Chargement...</TableCell>
              </TableRow>
            ) : orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>
                  <p className="font-mono text-sm">#{order.id.slice(0, 8)}</p>
                </TableCell>
                <TableCell>
                  <p>{order.shipping_city}</p>
                  <p className="text-sm text-stone-500">{order.items.length} articles</p>
                </TableCell>
                <TableCell className="font-semibold">{formatPrice(order.total)}</TableCell>
                <TableCell>{formatDate(order.created_at)}</TableCell>
                <TableCell>
                  <Select
                    value={order.status}
                    onValueChange={(value) => updateStatus(order.id, value)}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="confirmed">Confirmée</SelectItem>
                      <SelectItem value="processing">En préparation</SelectItem>
                      <SelectItem value="shipped">Expédiée</SelectItem>
                      <SelectItem value="delivered">Livrée</SelectItem>
                      <SelectItem value="cancelled">Annulée</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Quotes Management
const QuotesAdmin = () => {
  const { token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (e) {
      console.error("Error fetching quotes:", e);
    }
    setLoading(false);
  };

  const updateStatus = async (quoteId, status) => {
    try {
      await axios.put(
        `${API}/quotes/${quoteId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Statut mis à jour");
      fetchQuotes();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  return (
    <div className="space-y-6" data-testid="admin-quotes">
      <h1 className="text-3xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
        Demandes de devis
      </h1>

      <Card className="border-0 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contact</TableHead>
              <TableHead>Entreprise</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Statut</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">Chargement...</TableCell>
              </TableRow>
            ) : quotes.map((quote) => (
              <TableRow key={quote.id}>
                <TableCell>
                  <p className="font-medium">{quote.contact_name}</p>
                  <p className="text-sm text-stone-500">{quote.email}</p>
                </TableCell>
                <TableCell>{quote.company}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{quote.service_type}</Badge>
                </TableCell>
                <TableCell>{formatDate(quote.created_at)}</TableCell>
                <TableCell>
                  <Select
                    value={quote.status}
                    onValueChange={(value) => updateStatus(quote.id, value)}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="contacted">Contacté</SelectItem>
                      <SelectItem value="in_progress">En cours</SelectItem>
                      <SelectItem value="completed">Terminé</SelectItem>
                      <SelectItem value="rejected">Rejeté</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// CSV Import
const ImportCSV = () => {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/products/import-csv`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setResult(response.data);
      toast.success(`${response.data.imported} produits importés !`);
    } catch (e) {
      console.error("Import error:", e);
      toast.error("Erreur lors de l'import");
    }
    setUploading(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-import">
      <h1 className="text-3xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
        Importer des produits
      </h1>

      <Card className="border-0 shadow-sm p-8">
        <form onSubmit={handleUpload} className="space-y-6">
          <div>
            <Label htmlFor="csv">Fichier CSV</Label>
            <Input
              id="csv"
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0])}
              className="mt-2"
              data-testid="csv-input"
            />
          </div>

          <div className="bg-stone-50 rounded-xl p-4">
            <p className="font-semibold text-[#1D3557] mb-2">Format attendu :</p>
            <code className="text-sm text-stone-600">
              name,description,price,category_id,image_url,supplier,customizable,min_quantity,stock,sku,tags
            </code>
          </div>

          <Button
            type="submit"
            disabled={!file || uploading}
            className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full"
            data-testid="upload-btn"
          >
            {uploading ? "Import en cours..." : "Importer les produits"}
          </Button>
        </form>

        {result && (
          <div className="mt-6 p-4 bg-green-50 rounded-xl">
            <p className="font-semibold text-green-800">
              {result.imported} produits importés avec succès
            </p>
            {result.errors?.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-red-600">Erreurs :</p>
                <ul className="text-sm text-red-500">
                  {result.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

// Main Admin Page
const AdminPage = () => {
  const location = useLocation();

  const navItems = [
    { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/products", label: "Produits", icon: Package },
    { path: "/admin/suppliers", label: "Fournisseurs", icon: Globe },
    { path: "/admin/toptex", label: "Catalogue TopTex", icon: TrendingUp },
    { path: "/admin/orders", label: "Commandes", icon: ShoppingCart },
    { path: "/admin/quotes", label: "Devis", icon: FileText },
    { path: "/admin/import", label: "Import CSV", icon: Upload },
  ];

  return (
    <div className="min-h-screen bg-[#F4F1EA]" data-testid="admin-page">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-[#1D3557] min-h-screen p-6 hidden lg:block">
          <div className="flex items-center gap-3 mb-10">
            <img 
              src="https://customer-assets.emergentagent.com/job_moreati-preview/artifacts/abz6ktff_Copie%20de%20nouveau%20logo%20moreati%20%281%29.png" 
              alt="MOREATI" 
              className="h-8 object-contain brightness-0 invert"
            />
            <span className="text-xl font-bold text-white">Admin</span>
          </div>

          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  location.pathname === item.path
                    ? "bg-white/20 text-white"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-6 left-6 right-6">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white transition-colors"
            >
              ← Retour au site
            </Link>
          </div>
        </aside>

        {/* Mobile Nav */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 p-2 z-50">
          <div className="flex justify-around">
            {navItems.slice(0, 4).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg ${
                  location.pathname === item.path ? "text-[#C65D47]" : "text-stone-500"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-xs">{item.label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 p-8 pb-24 lg:pb-8">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="products" element={<ProductsAdmin />} />
            <Route path="suppliers" element={<AdminSuppliersPage />} />
            <Route path="toptex" element={<TopTexCatalogPage />} />
            <Route path="orders" element={<OrdersAdmin />} />
            <Route path="quotes" element={<QuotesAdmin />} />
            <Route path="import" element={<ImportCSV />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
